import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Badge,
  Table,
  Button,
  Popover,
  PopoverHeader,
  PopoverBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { getOrgSummary } from "../service";
import { updateRealOrder } from "../../project/service";
import staticData from "../data/org_summary_sample.json";
import ActivityLog from "./ActivityLog";

const USE_STATIC_DATA = false;

const OrgSummaryPane = ({ organizationId, onClose }) => {
  const [data, setData] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });
  const [selectedProject, setSelectedProject] = useState("");
  const [showActivityLog, setShowActivityLog] = useState(false);

  const toggleActivityLog = () => setShowActivityLog(!showActivityLog);

  useEffect(() => {
    if (organizationId) {
      if (USE_STATIC_DATA) {
        setData(staticData);
      } else {
        getOrgSummary(organizationId).then((res) => setData(res));
      }
    }
  }, [organizationId]);

  const togglePopover = () => setPopoverOpen(!popoverOpen);

  const formatCurrency = (value) =>
    value === 0 ? "-" : `₹${(value / 100000).toFixed(2)}L`;

  const getRelativeTime = (input) => {
    if (!input) return "-";
    const date = new Date(input);
    const now = new Date();
    const diff = (date.getTime() - now.getTime()) / 1000;

    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

    const ranges = [
      { unit: "year", seconds: 31536000 },
      { unit: "month", seconds: 2592000 },
      { unit: "day", seconds: 86400 },
      { unit: "hour", seconds: 3600 },
      { unit: "minute", seconds: 60 },
      { unit: "second", seconds: 1 },
    ];

    for (let range of ranges) {
      if (Math.abs(diff) > range.seconds || range.unit === "second") {
        const delta = Math.round(diff / range.seconds);
        return rtf.format(delta, range.unit);
      }
    }
  };

  const onRealOrderChange = (projectId, value) => {
    updateRealOrder(projectId, value)
      .then(() => {
        setData((prev) => ({
          ...prev,
          projects: prev.projects.map((proj) =>
            proj.id === projectId ? { ...proj, real_order: value } : proj
          ),
        }));
      })
      .catch((err) => console.error("Failed to update real_order", err));
  };

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const sortProjects = (projects) => {
    return [...projects].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "last_activity_at") {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortConfig.key === "real_order") {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === "string" || typeof bVal === "string") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
      } else {
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  return (
    <>
      <Offcanvas
        isOpen={!!organizationId}
        toggle={onClose}
        direction="end"
        style={{ width: "50%", top: "0", marginTop: 0 }}
        className="border-start position-fixed"
      >
        <OffcanvasHeader toggle={onClose} className="border-bottom pb-2 mb-0">
          {data?.name || "Loading..."}
        </OffcanvasHeader>

        <div className="d-flex justify-content-between align-items-center bg-light px-3 py-2 border-bottom">
          <Badge
            color="secondary"
            pill
            className="text-uppercase small px-3 py-1"
          >
            {data?.status}
          </Badge>
          <div>
            <Button
              id="usersBtn"
              color="light"
              size="sm"
              className="border"
              onClick={togglePopover}
            >
              <i className="fas fa-user"></i>
            </Button>
            <Popover
              placement="left"
              isOpen={popoverOpen}
              target="usersBtn"
              toggle={togglePopover}
            >
              <PopoverHeader>
                Users
                <Button close className="ms-auto" onClick={togglePopover} />
              </PopoverHeader>
              <PopoverBody>
                <Table bordered size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th className="bg-secondary text-white">Name</th>
                      <th className="bg-secondary text-white">Mobile</th>
                      <th className="bg-secondary text-white">Price Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.users || []).map((u, idx) => (
                      <tr key={idx}>
                        <td>{u.name}</td>
                        <td>{u.mobile}</td>
                        <td>{u.view_price ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </PopoverBody>
            </Popover>
          </div>
        </div>

        <OffcanvasBody className="p-3">
          <Table
            bordered
            responsive
            hover
            size="sm"
            style={{ fontSize: "0.95rem" }}
          >
            <thead>
              <tr>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("name")}
                >
                  Project {renderSortIcon("name")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("value")}
                >
                  Value {renderSortIcon("value")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("status")}
                >
                  Status {renderSortIcon("status")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("last_activity_at")}
                >
                  Last Activity {renderSortIcon("last_activity_at")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("real_order")}
                >
                  Type {renderSortIcon("real_order")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("score")}
                >
                  Score {renderSortIcon("score")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortProjects(data?.projects || []).map((proj) => (
                <tr key={proj.id} style={{ whiteSpace: "nowrap" }}>
                  <td>
                    <a
                      href={`/projects/${proj.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark text-decoration-none"
                    >
                      {proj.name}
                    </a>{" "}
                    <span className="text-AAAAAA">#{proj.id}</span>
                  </td>
                  <td>{formatCurrency(proj.value)}</td>
                  <td>{proj.status}</td>
                  <td>{getRelativeTime(proj.last_activity_at)}</td>
                  <td>
                    <UncontrolledDropdown>
                      <DropdownToggle
                        color="light"
                        size="sm"
                        className={`px-2 py-0 border rounded-2 w-auto h-auto ${
                          proj.real_order
                            ? "bg-success-subtle text-success"
                            : "bg-secondary-subtle text-muted"
                        }`}
                      >
                        {proj.real_order ? "Order" : "Test"}
                      </DropdownToggle>
                      <DropdownMenu end>
                        <DropdownItem
                          active={proj.real_order}
                          onClick={() => onRealOrderChange(proj.id, true)}
                        >
                          Order
                        </DropdownItem>
                        <DropdownItem
                          active={!proj.real_order}
                          onClick={() => onRealOrderChange(proj.id, false)}
                        >
                          Test
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </td>
                  <td
                    onClick={() => {
                      setSelectedProject(proj);
                      toggleActivityLog();
                    }}
                  >
                    {proj.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={showActivityLog} toggle={toggleActivityLog} size="xl">
        <ModalHeader>{selectedProject.name}</ModalHeader>
        <ModalBody>
          <ActivityLog
            projectId={selectedProject.id}
            getRelativeTime={getRelativeTime}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default OrgSummaryPane;
